/**
 * M11 accommodation shapes the web reads. Everything on the wire comes from
 * `@seagames/contracts` (`accommodation.ts`, S0) — re-exported here so the
 * components keep one import. The web-only pieces below are NOT wire shapes,
 * except `LodgingBuildingsPage` (S2's list wrapper, not exported by S0).
 */
import type { LodgingBuildingListItem, RoomAssignmentSummary, RoomingPersonState } from "@seagames/contracts";

export {
	ACCOMMODATION_FLAGS,
	AUTO_ASSIGN_ORGS_MAX,
	LODGING_BEDS_MAX,
	LODGING_BUILDING_CODE_MAX,
	LODGING_BUILDING_CODE_PATTERN,
	LODGING_BEDS_MIN,
	LODGING_BLOCKS_MAX,
	LODGING_BUILDING_TYPES,
	LODGING_FLOOR_MAX,
	LODGING_FLOOR_MIN,
	LODGING_OOS_NOTE_MAX,
	LODGING_ROOMS_GENERATE_MAX,
	LODGING_ROOMS_PER_FLOOR_MAX,
	LODGING_ROOM_NO_MAX,
	ROOM_CHECK_IDS_MAX,
	ROOM_STATES,
	ROOMING_PEOPLE_PAGE_SIZE_DEFAULT,
	ROOMING_PERSON_STATES,
	STAY_DEFAULT_DAYS_AFTER_END,
	STAY_DEFAULT_DAYS_BEFORE_START,
	type AccommodationFlag,
	type AccommodationSummary,
	type AccreditationAccommodation,
	type AutoAssignSkipReason,
	type CreateLodgingBuildingBody,
	type DeskGuest,
	type DeskGuestPage,
	type DeskLookup,
	type FloorMap,
	type FloorMapBed,
	type FloorMapRoom,
	type FloorSaveResult,
	type GenerateRoomsBody,
	type GenerateRoomsResult,
	type LodgingBuildingConfig,
	type LodgingBuildingListItem,
	type LodgingBuildingOption,
	type LodgingBuildingRef,
	type LodgingBuildingType,
	type LodgingRoomDetail,
	type LodgingSex,
	type PatchLodgingBuildingBody,
	type PatchRoomsBody,
	type ReleaseReservationsResult,
	type ReserveRoomsBody,
	type ReserveRoomsResult,
	type RoomAssignmentSummary,
	type RoomAutoAssignPick,
	type RoomAutoAssignResult,
	type RoomAutoAssignSuggestion,
	type RoomCheckAction,
	type RoomOption,
	type RoomCheckRefusal,
	type RoomCheckResult,
	type RoomEditRefusal,
	type RoomEditResult,
	type SaveFloorBody,
	type RoomingContingent,
	type RoomingOrgRef,
	type RoomingPeoplePage,
	type RoomingPeopleSortKey,
	type RoomingPerson,
	type RoomingPersonState,
	type RoomState,
	type StayGroup,
	type StayGroupsResponse,
	type StaySource,
} from "@seagames/contracts";

/** `GET /lodging-buildings` (S2): occupancy on `date`, ≤ 200 rows. */
export interface LodgingBuildingsPage {
	date: string;
	items: LodgingBuildingListItem[];
	truncated: boolean;
}

/** A stay `[from, to)`: `from` = first night, `to` = check-out day. Dates in the games' zone. */
export interface Stay {
	from: string;
	to: string;
}

/** An assignment's stay as a `Stay`. */
export const stayOf = (a: Pick<RoomAssignmentSummary, "stay_from" | "stay_to">): Stay => ({ from: a.stay_from, to: a.stay_to });

/** A person's state from an assignment alone (desk rows carry the assignment, not the state). */
export const assignmentState = (a: Pick<RoomAssignmentSummary, "checked_in_at" | "checked_out_at">): RoomingPersonState =>
	a.checked_out_at ? "OUT" : a.checked_in_at ? "IN" : "ASSIGNED";


/** Her contingent page's page sizes (default ROOMING_PEOPLE_PAGE_SIZE_DEFAULT = 50). */
export const ROOMING_PAGE_SIZES = [25, 50, 100] as const;
